import React from 'react'
import { createPortal } from 'react-dom'
import { HappyTaskLoadingLogo } from '../../icons/HappyTaskLoadingLogo'
import './LogoScreen.scss'

export type LogoScreenVariant = 'fill' | 'below-titlebar' | 'full'

export interface LogoScreenProps {
  /**
   * Wie viel der Schirm abdeckt:
   *
   * - `fill` — füllt seinen Elternteil (Vorgabe). Für Zweige, die STATT der
   *   Seite gerendert werden.
   * - `below-titlebar` — liegt fest über allem unter der Titelleiste.
   * - `full` — der **Vorhang**: deckt das ganze Fenster ab, die Titelleiste
   *   eingeschlossen — nur die **Ampel** bleibt frei. Für den Start.
   */
  variant?: LogoScreenVariant
  /** Optionaler Text unter dem Logo. */
  children?: React.ReactNode
}

/**
 * Der HappyTask-Logo-Schirm — Bildmarke und Schriftzug, mittig.
 *
 * Er stand vorher sechsmal als kopierter Inline-Style in `ProtectedRoute` und
 * `SetupRoute`, jedes Mal mit eigenem `@keyframes fadeIn` im `<style>`-Block
 * daneben. Zusammengeführt, weil er mit dem Aufschub (`useWindowRevealHold`)
 * eine siebte Verwendung bekommen hat — und weil sich dabei zwei Dinge
 * herausgestellt haben, die sechsmal nachzuziehen niemand gemerkt hätte:
 *
 * **Er blendet nicht mehr ein.** Die `fadeIn`-Kurve stammt aus einer Zeit, in
 * der dieser Schirm das ERSTE war, was man sah. Als Vorhang über einer bereits
 * gerenderten Seite wurde daraus das Gegenteil: 0,3s lang sah man die halb
 * aufgebaute App durch das erscheinende Logo hindurch.
 *
 * **Die Ampel bleibt frei.** Ein Vorhang über die Fensterknöpfe nimmt einer App
 * das Wichtigste, was eine App von einer Webseite unterscheidet — man muss sie
 * jederzeit schließen können.
 *
 * @example
 * // statt der Seite
 * <LogoScreen />
 *
 * // als Vorhang über der ladenden App
 * <LogoScreen variant="full" />
 */
export const LogoScreen: React.FC<LogoScreenProps> = ({
  variant = 'fill',
  children,
}) => {
  if (variant === 'full') {
    /*
      PER PORTAL an den `body`, und das ist tragend — kein `z-index` hätte es
      sonst getan.

      `.window-content` trägt `z-index: 4` und erzeugt damit einen eigenen
      Stacking-Context (window.scss). Alles, was darin liegt, ist gegenüber den
      Geschwistern des Fenster-Rahmens auf diese 4 gedeckelt — und dort
      rangieren die Rahmen-Silhouette auf 5 und die Tab-Beschriftungen auf 6.
      Ein Vorhang mit `z-index: 10001` blieb deshalb UNTER den Tabs, egal wie
      hoch die Zahl war. Am `body` liegt er im Wurzel-Kontext und über allem.

      Dieselbe Bauart wie beim **Drag Handle** des Projekt-Canvas (ADR 0010),
      aus demselben Grund: Wer über allem liegen muss, darf in keinem Kasten
      stecken.
    */
    return createPortal(
      <div className="logo-screen logo-screen--full">
        {/*
          Zwei Kästen statt eines: Der obere deckt die Titelleiste ab — aber
          erst NACH der Ampel, die links davon frei bleibt. Mit einem einzigen
          Kasten ginge das nicht, denn unter der Ampel muss weiter abgedeckt
          werden.
        */}
        <div className="logo-screen__chrome" />
        <div className="logo-screen__body">
          <HappyTaskLoadingLogo />
          {children}
        </div>
      </div>,
      document.body,
    )
  }

  return (
    <div className={`logo-screen logo-screen--${variant}`}>
      <HappyTaskLoadingLogo />
      {children}
    </div>
  )
}
